// 🔤 자막 폰트 카탈로그(SSOT) — font-harness(천지인) 접합 1단계(회장님 2026-07-10 승인).
//   자막 번인(mediabunnyRender)·미리보기가 같은 정의를 쓴다. 커스텀 폰트는 public/fonts/ 의
//   정적 파일을 FontFace 로 렌더 직전 레이지 로드(렌더 안 하는 유저에겐 0 비용, 실패=기본 폴백).
//   ★OFL 1.1: 원본 TTF 무수정 사용이라 "Cheonjiin" 이름 사용 가능 — 자모를 고친 파생판을 만들면
//     Reserved Font Name 제약으로 새 이름 필수(font-harness SERVICE_ADAPTER.md 참조).
export interface CaptionFontDef {
  id: string;
  label: string;
  /** CSS family명(FontFace 등록명). null=기본 스택만 사용 */
  family: string | null;
  /** public/ 기준 상대 경로(커스텀 폰트 파일) */
  file?: string;
  /** ctx.font weight — 미지정 900. 기하 폰트는 합성볼드가 획을 뭉개 폰트별 지정 */
  weight?: number;
  /** 자간(px, 360폭 기준 계수) — 미지정 -0.4(기존 자막 정합). 기하 폰트는 0 권장 */
  letterSpacing?: number;
  /** 한글 완성형 전용(숫자·영문·부호는 뒤 스택 폴백) — UI 안내용 */
  hangulOnly?: boolean;
  /** 피커 optgroup 카테고리(임팩트·제목/고딕·본문/명조·세리프/둥근·귀여움/손글씨·필기/레트로·개성/일본어·해외). 미지정=그룹 없음 */
  category?: string;
}

/** 자막 폰트 피커 카테고리 표시 순서 */
export const CAPTION_FONT_CATEGORIES = [
  "임팩트·제목",
  "고딕·본문",
  "명조·세리프",
  "둥근·귀여움",
  "손글씨·필기",
  "레트로·개성",
  "일본어·해외",
] as const;

/** 기존 자막 스택(불변) — 커스텀 폰트는 이 앞에 얹힌다(완성형 외 글자 폴백 보장) */
export const DEFAULT_CAPTION_STACK = `"Noto Sans KR", "Pretendard Variable", "Apple SD Gothic Neo", sans-serif`;

export const CAPTION_FONTS: CaptionFontDef[] = [
  { id: "default", label: "기본 (고딕)", family: null },
  {
    id: "cheonjiin",
    label: "천지인 (기하 한글)",
    family: "Cheonjiin",
    file: "fonts/Cheonjiin-v0.4.1.ttf",
    weight: 400,        // 막대 두께 80 고정 설계 — 합성볼드(900) 금지
    letterSpacing: 0,   // 20유닛 그리드 기하 — 음수 자간이 원·막대를 겹치게 함
    hangulOnly: true,
    category: "레트로·개성",
  },
  {
    id: "blackhansans",
    label: "검은고딕 (임팩트)",
    family: "BlackHanSans",
    file: "fonts/BlackHanSans.ttf",
    weight: 400,        // 단일 웨이트 울트라블랙 — 합성볼드(900)가 획을 뭉갬
    letterSpacing: 0,   // 자면이 꽉 차는 서체 — 음수 자간이 획을 겹치게 함
    category: "임팩트·제목",
  },
  {
    // 🌐 JP 수출 배치(2026-07-12) — 기본 스택(NotoSansKR)에 히라가나/가타카나가 없어 일본어
    //   스타일의 필수 선결. 구글 폰트 공식 GitHub ofl/notosansjp 원본 무수정(OFL 1.1)·가변
    //   TTF(wght 100~900) — FontFace weight 900 = Black 인스턴스(JP 텔롭 정석 굵기).
    id: "notosansjp",
    label: "Noto Sans JP (일본어 고딕)",
    family: "NotoSansJP",
    file: "fonts/NotoSansJP.ttf",
    weight: 900,
    letterSpacing: 0,   // 일본어 조판은 음수 자간 금지(가나 획 겹침)
    category: "일본어·해외",
  },

  // 🔤 무료 한글 폰트 대량 배치(회장님 2026-07-14) — 회사 모노레포 폰트 저장고에서 라이선스
  //   전수검증(상업+영상 번인+웹폰트 self-host 허용) 통과한 safe 75패밀리. 전부 woff/woff2 self-host.
  //   ★weight = 해당 파일의 실제(최대) 웨이트로 명시 — css 토큰과 FontFace 등록을 일치시켜 합성볼드(획 뭉갬) 회피.
  //   ★letterSpacing 0 — 디스플레이/굵은 서체는 음수 자간이 획을 겹치게 함(기존 커스텀 폰트와 동일 원칙).
  //   비(非)OFL 폰트도 영상 번인·임베딩은 허용(원본 파일 다운로드 노출만 금지 — self-host 렌더는 안전).
  { id: "gana-chocolate", label: "가나초콜릿", family: "Gana Chocolate", file: "fonts/GanaChocolate.woff2", weight: 400, letterSpacing: 0, category: "임팩트·제목" },
  { id: "gasoek-one", label: "가석원", family: "Gasoek One", file: "fonts/gasoek-one.woff2", weight: 400, letterSpacing: 0, category: "임팩트·제목" },
  { id: "the-jamsil", label: "더잠실체", family: "The Jamsil", file: "fonts/TheJamsil_6ExtraBold.woff2", weight: 800, letterSpacing: 0, category: "임팩트·제목" },
  { id: "do-hyeon", label: "도현", family: "Do Hyeon", file: "fonts/do-hyeon.woff2", weight: 400, letterSpacing: 0, category: "임팩트·제목" },
  { id: "bagel-fat-one", label: "베이글 팻 원", family: "Bagel Fat One", file: "fonts/bagel-fat-one.woff2", weight: 400, letterSpacing: 0, category: "임팩트·제목" },
  { id: "yeogi-ottae-jalnan", label: "여기어때 잘난체", family: "Yeogi Ottae Jalnan", file: "fonts/YeogiOttaeJalnan.woff", weight: 400, letterSpacing: 0, category: "임팩트·제목" },
  { id: "yeogi-ottae-jalnan-gothic", label: "여기어때 잘난체 고딕", family: "Yeogi Ottae Jalnan Gothic", file: "fonts/YeogiOttaeJalnanGothic.woff", weight: 400, letterSpacing: 0, category: "임팩트·제목" },
  { id: "one-store-mobile-pop", label: "원스토어 모바일팝", family: "One Store Mobile Pop", file: "fonts/OneStoreMobilePop.woff2", weight: 400, letterSpacing: 0, category: "임팩트·제목" },
  { id: "jua", label: "주아", family: "Jua", file: "fonts/Jua-Regular.woff2", weight: 400, letterSpacing: 0, category: "임팩트·제목" },
  { id: "gmarket-sans", label: "지마켓산스", family: "Gmarket Sans", file: "fonts/GmarketSansBold.woff", weight: 700, letterSpacing: 0, category: "임팩트·제목" },
  { id: "cafe24-dangeonghae", label: "카페24 단정해", family: "Cafe24 Dangeonghae", file: "fonts/Cafe24Danjeonghae.woff", weight: 400, letterSpacing: 0, category: "임팩트·제목" },
  { id: "cafe24-dangdanghae", label: "카페24 당당해", family: "Cafe24 Dangdanghae", file: "fonts/Cafe24Dangdanghae.woff", weight: 400, letterSpacing: 0, category: "임팩트·제목" },
  { id: "cafe24-supermagic", label: "카페24 슈퍼매직", family: "Cafe24 SuperMagic", file: "fonts/Cafe24SuperMagic_Bold.woff", weight: 700, letterSpacing: 0, category: "임팩트·제목" },
  { id: "cafe24-ssukssuk", label: "카페24 쑥쑥", family: "Cafe24 Ssukssuk", file: "fonts/Cafe24Ssukssuk.woff", weight: 400, letterSpacing: 0, category: "임팩트·제목" },
  { id: "cafe24-proup", label: "카페24 프로업", family: "Cafe24 ProUp", file: "fonts/Cafe24ProUp.woff", weight: 400, letterSpacing: 0, category: "임팩트·제목" },
  { id: "cookie-run-bold", label: "쿠키런 볼드", family: "Cookie Run Bold", file: "fonts/CookieRun_Bold.woff", weight: 400, letterSpacing: 0, category: "임팩트·제목" },
  { id: "cookie-run-black", label: "쿠키런 블랙", family: "Cookie Run Black", file: "fonts/CookieRun_Black.woff", weight: 400, letterSpacing: 0, category: "임팩트·제목" },
  { id: "paperlogy", label: "페이퍼로지", family: "Paperlogy", file: "fonts/Paperlogy-9Black.woff", weight: 900, letterSpacing: 0, category: "임팩트·제목" },
  { id: "gothic-a1", label: "고딕 A1", family: "Gothic A1", file: "fonts/gothic-a1.woff2", weight: 400, letterSpacing: 0, category: "고딕·본문" },
  { id: "nanum-gothic", label: "나눔고딕", family: "Nanum Gothic", file: "fonts/nanum-gothic.woff2", weight: 400, letterSpacing: 0, category: "고딕·본문" },
  { id: "nanum-square", label: "나눔스퀘어", family: "Nanum Square", file: "fonts/NanumSquare_ExraBold.woff", weight: 800, letterSpacing: 0, category: "고딕·본문" },
  { id: "nanum-square-neo", label: "나눔스퀘어 네오", family: "Nanum Square Neo", file: "fonts/NanumSquareNeo-Variable.woff", weight: 900, letterSpacing: 0, category: "고딕·본문" },
  { id: "neurimbo-gothic", label: "느림보고딕", family: "Neurimbo Gothic", file: "fonts/neurimboGothicRegular.woff2", weight: 400, letterSpacing: 0, category: "고딕·본문" },
  { id: "bookk-gothic", label: "부크크고딕", family: "Bookk Gothic", file: "fonts/BookkGothic_Light.woff", weight: 300, letterSpacing: 0, category: "고딕·본문" },
  { id: "suit", label: "수트", family: "SUIT", file: "fonts/SUIT-Variable.woff2", weight: 900, letterSpacing: 0, category: "고딕·본문" },
  { id: "asta-sans", label: "아스타 산스", family: "Asta Sans", file: "fonts/asta-sans.woff2", weight: 400, letterSpacing: 0, category: "고딕·본문" },
  { id: "one-store-mobile-gothic-body", label: "원스토어 모바일고딕", family: "One Store Mobile Gothic Body", file: "fonts/OneStoreMobileGothicBody_Bold.woff2", weight: 700, letterSpacing: 0, category: "고딕·본문" },
  { id: "one-store-mobile-gothic-title", label: "원스토어 모바일고딕 제목", family: "One Store Mobile Gothic Title", file: "fonts/OneStoreMobileGothicTitle.woff2", weight: 400, letterSpacing: 0, category: "고딕·본문" },
  { id: "cafe24-simple", label: "카페24 심플", family: "Cafe24 Simple", file: "fonts/Cafe24Simple.woff", weight: 400, letterSpacing: 0, category: "고딕·본문" },
  { id: "cafe24-proslim", label: "카페24 프로슬림", family: "Cafe24 ProSlim", file: "fonts/Cafe24ProSlim_700.woff", weight: 700, letterSpacing: 0, category: "고딕·본문" },
  { id: "freesentation", label: "프리젠테이션", family: "Freesentation", file: "fonts/FreesentationVF.woff2", weight: 900, letterSpacing: 0, category: "고딕·본문" },
  { id: "pretendard", label: "프리텐다드", family: "Pretendard", file: "fonts/pretendard-variable.woff2", weight: 900, letterSpacing: 0, category: "고딕·본문" },
  { id: "hs-jandari", label: "HS잔다리", family: "HS Jandari", file: "fonts/HSJandari-Regular.woff2", weight: 400, letterSpacing: 0, category: "고딕·본문" },
  { id: "ibm-plex-sans-kr", label: "IBM 플렉스 산스", family: "IBM Plex Sans KR", file: "fonts/ibm-plex-sans-kr.woff2", weight: 400, letterSpacing: 0, category: "고딕·본문" },
  { id: "gowun-batang", label: "고운바탕", family: "Gowun Batang", file: "fonts/gowun-batang.woff2", weight: 400, letterSpacing: 0, category: "명조·세리프" },
  { id: "nanum-myeongjo", label: "나눔명조", family: "Nanum Myeongjo", file: "fonts/nanum-myeongjo.woff2", weight: 400, letterSpacing: 0, category: "명조·세리프" },
  { id: "noto-serif-kr", label: "노토세리프", family: "Noto Serif KR", file: "fonts/noto-serif-kr.woff2", weight: 400, letterSpacing: 0, category: "명조·세리프" },
  { id: "diphylleia", label: "디필레이아", family: "Diphylleia", file: "fonts/diphylleia.woff2", weight: 400, letterSpacing: 0, category: "명조·세리프" },
  { id: "bookk-myungjo", label: "부크크명조", family: "Bookk Myungjo", file: "fonts/BookkMyungjo_Light.woff", weight: 300, letterSpacing: 0, category: "명조·세리프" },
  { id: "song-myung", label: "송명", family: "Song Myung", file: "fonts/song-myung.woff2", weight: 400, letterSpacing: 0, category: "명조·세리프" },
  { id: "hahmlet", label: "함렛", family: "Hahmlet", file: "fonts/hahmlet.woff2", weight: 400, letterSpacing: 0, category: "명조·세리프" },
  { id: "gowun-dodum", label: "고운돋움", family: "Gowun Dodum", file: "fonts/gowun-dodum.woff2", weight: 400, letterSpacing: 0, category: "둥근·귀여움" },
  { id: "nanum-square-round", label: "나눔스퀘어라운드", family: "Nanum Square Round", file: "fonts/NanumSquareRound_ExtraBold.woff", weight: 800, letterSpacing: 0, category: "둥근·귀여움" },
  { id: "dongle", label: "동글", family: "Dongle", file: "fonts/dongle.woff2", weight: 400, letterSpacing: 0, category: "둥근·귀여움" },
  { id: "recipe-korea", label: "레시피코리아", family: "Recipe Korea", file: "fonts/Recipekorea.woff", weight: 400, letterSpacing: 0, category: "둥근·귀여움" },
  { id: "baemin-yeonsung", label: "배민 연성체", family: "Baemin Yeonsung", file: "fonts/BM_YEONSUNG.woff", weight: 400, letterSpacing: 0, category: "둥근·귀여움" },
  { id: "cafe24-dongdong", label: "카페24 동동", family: "Cafe24 Dongdong", file: "fonts/Cafe24Dongdong.woff", weight: 400, letterSpacing: 0, category: "둥근·귀여움" },
  { id: "cafe24-surround", label: "카페24 써라운드", family: "Cafe24 Surround", file: "fonts/Cafe24Surround.woff", weight: 400, letterSpacing: 0, category: "둥근·귀여움" },
  { id: "cafe24-surroundair", label: "카페24 써라운드에어", family: "Cafe24 SurroundAir", file: "fonts/Cafe24SurroundAir.woff", weight: 400, letterSpacing: 0, category: "둥근·귀여움" },
  { id: "cafe24-anemone", label: "카페24 아네모네", family: "Cafe24 Anemone", file: "fonts/Cafe24Anemone.woff", weight: 400, letterSpacing: 0, category: "둥근·귀여움" },
  { id: "cafe24-anemoneair", label: "카페24 아네모네에어", family: "Cafe24 AnemoneAir", file: "fonts/Cafe24AnemoneAir.woff", weight: 400, letterSpacing: 0, category: "둥근·귀여움" },
  { id: "cookie-run-regular", label: "쿠키런", family: "Cookie Run Regular", file: "fonts/CookieRun_Regular.woff", weight: 400, letterSpacing: 0, category: "둥근·귀여움" },
  { id: "clipart-korea", label: "클립아트코리아", family: "ClipArt Korea", file: "fonts/ClipArtKorea_Bold.woff2", weight: 700, letterSpacing: 0, category: "둥근·귀여움" },
  { id: "kirang-haerang", label: "기랑해랑", family: "Kirang Haerang", file: "fonts/kirang-haerang.woff2", weight: 400, letterSpacing: 0, category: "손글씨·필기" },
  { id: "nanum-brush-script", label: "나눔손글씨 붓", family: "Nanum Brush Script", file: "fonts/nanum-brush-script.woff2", weight: 400, letterSpacing: 0, category: "손글씨·필기" },
  { id: "nanum-pen-script", label: "나눔손글씨 펜", family: "Nanum Pen Script", file: "fonts/nanum-pen-script.woff2", weight: 400, letterSpacing: 0, category: "손글씨·필기" },
  { id: "dokdo", label: "독도", family: "Dokdo", file: "fonts/dokdo.woff2", weight: 400, letterSpacing: 0, category: "손글씨·필기" },
  { id: "east-sea-dokdo", label: "동해독도", family: "East Sea Dokdo", file: "fonts/east-sea-dokdo.woff2", weight: 400, letterSpacing: 0, category: "손글씨·필기" },
  { id: "rix-inoo-ari-duri", label: "릭스 이누아리두리", family: "Rix Inoo Ari Duri", file: "fonts/RixInooAriDuri_Regular.woff2", weight: 400, letterSpacing: 0, category: "손글씨·필기" },
  { id: "cafe24-gowoonbam", label: "카페24 고운밤", family: "Cafe24 Gowoonbam", file: "fonts/Cafe24Gowoonbam.woff", weight: 400, letterSpacing: 0, category: "손글씨·필기" },
  { id: "cafe24-moyamoya", label: "카페24 모야모야", family: "Cafe24 MoyaMoya", file: "fonts/Cafe24MoyaMoya.woff", weight: 400, letterSpacing: 0, category: "손글씨·필기" },
  { id: "cafe24-moyamoyaface", label: "카페24 모야모야페이스", family: "Cafe24 MoyaMoyaFace", file: "fonts/Cafe24MoyaMoyaFace.woff", weight: 400, letterSpacing: 0, category: "손글씨·필기" },
  { id: "cafe24-shiningstar", label: "카페24 샤이닝스타", family: "Cafe24 ShiningStar", file: "fonts/Cafe24ShiningStar.woff", weight: 400, letterSpacing: 0, category: "손글씨·필기" },
  { id: "cafe24-ssongssong", label: "카페24 쏭쏭", family: "Cafe24 SsongSsong", file: "fonts/Cafe24SsongSsong.woff", weight: 400, letterSpacing: 0, category: "손글씨·필기" },
  { id: "poor-story", label: "푸어스토리", family: "Poor Story", file: "fonts/poor-story.woff2", weight: 400, letterSpacing: 0, category: "손글씨·필기" },
  { id: "gugi", label: "구기", family: "Gugi", file: "fonts/gugi.woff2", weight: 400, letterSpacing: 0, category: "레트로·개성" },
  { id: "grandiflora-one", label: "그랜디플로라", family: "Grandiflora One", file: "fonts/grandiflora-one.woff2", weight: 400, letterSpacing: 0, category: "레트로·개성" },
  { id: "ttangs-budae-jjigae", label: "땅스부대찌개", family: "Ttangs Budae Jjigae", file: "fonts/TtangsBudaeJjigae_Bold.woff2", weight: 700, letterSpacing: 0, category: "레트로·개성" },
  { id: "euljiro10yearslater", label: "배민 을지로10년후체", family: "Euljiro10YearsLater", file: "fonts/BM_EULJIRO_10yrs.woff2", weight: 400, letterSpacing: 0, category: "레트로·개성" },
  { id: "baemin-kkubulim", label: "배민 을지로꾸불림체", family: "Baemin Kkubulim", file: "fonts/BM_Kkubulim.woff", weight: 400, letterSpacing: 0, category: "레트로·개성" },
  { id: "seoul-namsan", label: "서울남산체", family: "Seoul Namsan", file: "fonts/SeoulNamsan_ExraBold.woff2", weight: 800, letterSpacing: 0, category: "레트로·개성" },
  { id: "seoul-notice", label: "서울알림체", family: "Seoul Notice", file: "fonts/SeoulNotice_Heavy.woff2", weight: 900, letterSpacing: 0, category: "레트로·개성" },
  { id: "seoul-hangang", label: "서울한강체", family: "Seoul Hangang", file: "fonts/SeoulHangang_ExtraBold.woff2", weight: 800, letterSpacing: 0, category: "레트로·개성" },
  { id: "orbit", label: "오르빗", family: "Orbit", file: "fonts/orbit.woff2", weight: 400, letterSpacing: 0, category: "레트로·개성" },
  { id: "cafe24-classic-type", label: "카페24 클래식타입", family: "Cafe24 Classic Type", file: "fonts/Cafe24ClassicType.woff", weight: 400, letterSpacing: 0, category: "레트로·개성" },
];

export function captionFontById(id?: string | null): CaptionFontDef {
  return CAPTION_FONTS.find((f) => f.id === id) ?? CAPTION_FONTS[0];
}

/** 제목(타이틀카드·상단 제목 띠·모션 제목) 렌더의 기존 폴백 스택 — 미선택 시 이 문자열이 그대로 유지돼야 회귀 0 */
export const TITLE_BASE_STACK = `"Noto Sans KR", "Apple SD Gothic Neo", sans-serif`;

/** 제목 ctx.font 조립 — id 미지정/default 면 기존 하드코딩과 **바이트 동일**(회귀 0).
 *  커스텀이면 폰트 고유 weight(합성볼드 회피) + family 를 기존 스택 앞에 얹는다. */
export function titleFontCss(id: string | null | undefined, px: number, baseWeight: number): string {
  const def = captionFontById(id);
  if (!def.family) return `${baseWeight} ${px}px ${TITLE_BASE_STACK}`;
  return `${def.weight ?? baseWeight} ${px}px "${def.family}", ${TITLE_BASE_STACK}`;
}

/** ctx.font 문자열 조립 — 커스텀 family를 기본 스택 앞에 얹는다(폴백 유지) */
export function captionFontCss(id: string | null | undefined, px: number): string {
  const def = captionFontById(id);
  const fams = def.family ? `"${def.family}", ${DEFAULT_CAPTION_STACK}` : DEFAULT_CAPTION_STACK;
  return `${def.weight ?? 900} ${px}px ${fams}`;
}

/** 자간 계수(360폭 기준) — drawCaption 의 `${coef * W / 360}px` 에 들어가는 값 */
export function captionLetterSpacing(id: string | null | undefined): number {
  const def = captionFontById(id);
  return def.letterSpacing ?? -0.4;
}

/** 📊 인포그래픽·라벨 글자 기본 스택 — 캔버스(`mediabunnyRender.drawInfographics`/`drawTextOverlays`)와
 *  React(`SceneInfographics`)가 같은 문자열을 쓴다. 미선택 시 이 문자열이 **바이트 그대로** 유지돼야 회귀 0. */
export const INFO_BASE_STACK = '"Pretendard","Noto Sans KR",system-ui,sans-serif';

/** 📊 인포그래픽·라벨 서체(A안 · 회장님 2026-09-06) — **제목 폰트를 따라간다**(별도 축 없음).
 *  id 미지정/default 면 `INFO_BASE_STACK` 만(회귀 0). 커스텀이면 family 를 앞에 얹고 weight 는 폰트 고유값
 *  (단일 웨이트 파일의 합성볼드 회피 — `titleFontCss` 와 같은 정책). */
export function infoFontFamily(id: string | null | undefined): string {
  const def = captionFontById(id);
  return def.family ? `"${def.family}", ${INFO_BASE_STACK}` : INFO_BASE_STACK;
}

/** 캔버스 ctx.font 조립 — 인포그래픽·라벨 전용(기본=`${baseWeight} ${px}px ${INFO_BASE_STACK}` 바이트 동일). */
export function infoFontCss(id: string | null | undefined, px: number, baseWeight: number): string {
  const def = captionFontById(id);
  if (!def.family) return `${baseWeight} ${px}px ${INFO_BASE_STACK}`;
  return `${def.weight ?? baseWeight} ${px}px "${def.family}", ${INFO_BASE_STACK}`;
}

/** React 스타일 — 인포그래픽 노드가 받는 서체 묶음. 기본 폰트면 undefined(노드가 기본 스택·주어진 weight 그대로). */
export interface InfoFontStyle { family: string; weight?: number }
export function infoFontStyle(id: string | null | undefined): InfoFontStyle | undefined {
  const def = captionFontById(id);
  return def.family ? { family: infoFontFamily(def.id), weight: def.weight } : undefined;
}

const loadedFontIds = new Set<string>();

/** 파일 확장자 → FontFace src format 힌트. woff2/woff/otf 파일에 truetype 힌트를 붙이면
 *  힌트 불일치로 브라우저가 소스를 스킵 → 조용한 폴백(로드 실패). 확장자로 정확히 도출한다. */
function fontSrcFormat(file: string): string {
  const ext = file.toLowerCase().split(".").pop();
  switch (ext) {
    case "woff2": return "woff2";
    case "woff":  return "woff";
    case "otf":   return "opentype";
    default:      return "truetype"; // ttf 및 기타
  }
}

/** 커스텀 폰트 파일 FontFace 로드(캐시·실패 무해 — 기본 스택 폴백으로 렌더 계속) */
/**
 * 🔤 **family 문자열 → 카탈로그 정의** — 저장분이 family 로 적히기 때문에(`captionFontFamily`·
 * `titleFontFamily` 와 같은 축) 캔버스 렌더처럼 **id 가 필요한 쪽**이 건너오는 다리다.
 * ★없는 family 는 `undefined`(fail-open)★ — 호출부가 종전 폰트로 폴백한다(로드 실패와 같은 처분).
 */
export function captionFontByFamily(family?: string | null): CaptionFontDef | undefined {
  const f = String(family ?? "").trim();
  return f ? CAPTION_FONTS.find((d) => d.family === f) : undefined;
}

export async function ensureCaptionFontFile(id?: string | null): Promise<void> {
  const def = captionFontById(id);
  if (!def.file || !def.family || loadedFontIds.has(def.id)) return;
  if (typeof document === "undefined" || typeof FontFace === "undefined") return;
  try {
    const face = new FontFace(def.family, `url(/${def.file}) format("${fontSrcFormat(def.file)}")`, {
      weight: String(def.weight ?? 400),
    });
    await face.load();
    document.fonts.add(face);
    loadedFontIds.add(def.id);
  } catch (e) {
    console.warn("[caption-fonts] 로드 실패 — 기본 폰트로 폴백", def.id, e);
  }
}
